import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Target, Clock, Flame, UserPlus, ShieldCheck, ArrowRight, Send, Users, MessageSquare, FileText, Loader2, Search, Compass, Network, Share2, TrendingUp } from "lucide-react";

const KIND_ICON = { outreach: Send, research: Search, conversation: MessageSquare, content: FileText, strategy: Target, experiment: Loader2, control: ShieldCheck };

const DigestCard = ({ icon: Icon, tint, label, title, sub, body, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="text-left rounded-xl border border-white/10 bg-[#0B0F17] p-3 hover:border-white/20 transition-all">
    <div className="flex items-center gap-1.5"><Icon size={13} className={tint} /><span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span></div>
    <p className="kz-head text-sm font-bold text-slate-100 mt-1.5 line-clamp-2">{title}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{sub}</p>}
    {body && <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 leading-snug">{body}</p>}
  </button>
);

const MiniStat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-white/[0.06] bg-[#0B0F17] p-3">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="kz-head text-lg font-extrabold text-emerald-400 mt-0.5">{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{sub}</div>}
  </div>
);

const Metric = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="kz-fade-up rounded-2xl border border-white/10 bg-[#121824] p-5 hover:border-white/20 transition-all">
    <Icon size={18} className={accent} />
    <div className="kz-head text-3xl font-extrabold mt-3">{value}</div>
    <div className="text-xs text-slate-400 mt-1 font-medium">{label}</div>
  </div>
);

export const Today = ({ goOpportunities, goApprove }) => {
  const [data, setData] = useState(null);
  const [autoSent, setAutoSent] = useState([]);
  const [recap, setRecap] = useState(null);
  const [digest, setDigest] = useState(null);
  useEffect(() => { api.getToday().then(setData); api.autoSentLog().then(setAutoSent).catch(() => {}); api.dailyRecap().then(setRecap).catch(() => {}); api.todayDigest().then(setDigest).catch(() => {}); }, []);
  if (!data) return <div className="max-w-6xl mx-auto h-40 animate-pulse bg-white/5 rounded-2xl" />;
  const m = data.metrics;
  const pullBack = async (id) => { await api.rejectAction(id); setAutoSent((a) => a.filter((x) => x.id !== id)); };

  return (
    <div data-testid="screen-today" className="max-w-6xl mx-auto">
      <div className="kz-fade-up">
        <p className="text-sm text-emerald-400 font-semibold tracking-wide">TODAY</p>
        <h1 className="kz-head text-3xl md:text-5xl font-extrabold tracking-tight mt-2">What KURT is doing now.</h1>
        <p className="text-slate-300 mt-3 text-base md:text-lg max-w-2xl leading-relaxed">
          <b className="text-slate-50">{m.opportunities}</b> live opportunities, <b className="text-amber-400">{m.pending_nod}</b> actions need your nod, and <b className="text-emerald-400">{m.hot_conversations}</b> hot conversations in play.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-8">
        <Metric testid="metric-opps" icon={Target} label="Opportunities" value={m.opportunities} accent="text-blue-400" />
        <Metric testid="metric-pending" icon={Clock} label="Pending your nod" value={m.pending_nod} accent="text-amber-400" />
        <Metric testid="metric-hot" icon={Flame} label="Hot conversations" value={m.hot_conversations} accent="text-red-400" />
        <Metric testid="metric-members" icon={UserPlus} label="New members" value={m.new_members} accent="text-emerald-400" />
      </div>

      {digest && (
        <div data-testid="command-digest" className="mt-6 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.07] to-transparent p-5 kz-fade-up">
          <div className="flex items-center gap-2 mb-4"><Compass size={16} className="text-emerald-400" /><span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Command center — today's biggest moves</span></div>
          <div className="grid md:grid-cols-3 gap-3">
            <DigestCard icon={Network} tint="text-amber-400" label="Top multiplier node" testid="digest-node"
              title={digest.top_node?.name || "—"} sub={digest.top_node ? `~${digest.top_node.reach || 0} SMEs · mult ${digest.top_node.multiplier || 0}` : "Hunt multipliers on Opportunities"} body={digest.top_node?.angle} onClick={goOpportunities} />
            <DigestCard icon={Share2} tint="text-blue-400" label="Hottest cluster" testid="digest-cluster"
              title={digest.hottest_cluster?.name || "—"} sub={digest.hottest_cluster ? `win: ${digest.hottest_cluster.hub} · ${digest.hottest_cluster.priority}` : "Map clusters on Opportunities"} body={digest.hottest_cluster?.play} onClick={goOpportunities} />
            <DigestCard icon={TrendingUp} tint="text-teal-400" label="Freshest trend" testid="digest-trend"
              title={digest.freshest_trend?.headline || "—"} sub={digest.freshest_trend?.signal} body={digest.freshest_trend?.tactic} onClick={goOpportunities} />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <MiniStat testid="digest-referral" label="Referral £/mo" value={`£${digest.referral.monthly_commission}`} sub={`${digest.referral.active_members} active members`} />
            <MiniStat testid="digest-covers" label="Cover members" value={digest.covers.joined} sub={`viral ${digest.covers.viral_coefficient} · ${digest.covers.total} covers`} />
            <MiniStat testid="digest-seo" label="SEO readers" value={digest.seo.readers.toLocaleString()} sub={`${digest.seo.articles} articles · ${digest.seo.members} members`} />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#121824] p-6 kz-fade-up">
          <div className="flex items-center justify-between mb-4">
            <h2 className="kz-head text-lg font-bold">What KURT did / is proposing</h2>
            <span className="flex items-center gap-1.5 text-xs text-emerald-400"><ShieldCheck size={13} /> Risk {m.risk_score}%</span>
          </div>
          {data.activity.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Quiet so far. Send KURT to Opportunities to get moving.</p>
          ) : (
            <div className="space-y-1">
              {data.activity.map((a) => {
                const Icon = KIND_ICON[a.kind] || ShieldCheck;
                const running = a.status === "running";
                return (
                  <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-start gap-3 py-3 border-b border-white/[0.05] last:border-0">
                    <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${running ? "bg-amber-500/15" : "bg-emerald-500/15"}`}>
                      <Icon size={15} className={running ? "text-amber-400 animate-spin" : "text-emerald-400"} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-200 leading-snug">{a.text}</p>
                      <span className="text-[11px] text-slate-500 kz-mono">{a.time} · {a.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-blue-500/25 bg-blue-500/[0.06] p-6 kz-fade-up">
            <div className="flex items-center gap-2 text-blue-400"><Target size={16} /><span className="text-xs font-bold uppercase tracking-wider">Find buyers</span></div>
            <p className="text-sm text-slate-300 mt-3 leading-relaxed">Point KURT at a target and he'll hunt fresh UK prospects.</p>
            <button data-testid="today-goto-opps" onClick={goOpportunities} className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] text-slate-100 font-semibold text-sm hover:bg-white/[0.1] transition-all">Open Opportunities <ArrowRight size={15} /></button>
          </div>
          <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-6 kz-fade-up">
            <div className="flex items-center gap-2 text-amber-400"><Clock size={16} /><span className="text-xs font-bold uppercase tracking-wider">Needs your nod</span></div>
            <p className="text-sm text-slate-300 mt-3 leading-relaxed">{m.pending_nod} compliance-checked actions are queued. One tap each.</p>
            <button data-testid="today-goto-approve" onClick={goApprove} className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-[#0B0F17] font-semibold text-sm hover:bg-amber-400 transition-all">Review & approve <ArrowRight size={15} /></button>
          </div>

          {autoSent.length > 0 && (
            <div data-testid="auto-sent-log" className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-6 kz-fade-up">
              <div className="flex items-center gap-2 text-emerald-400"><Send size={16} /><span className="text-xs font-bold uppercase tracking-wider">KURT sent automatically</span></div>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">{autoSent.length} message{autoSent.length > 1 ? "s" : ""} went out on auto-send. Pull any back.</p>
              <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {autoSent.map((a) => (
                  <div key={a.id} data-testid={`auto-sent-${a.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#0B0F17] border border-white/[0.06] px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-100 truncate">{a.target || a.title}</p>
                      <p className="text-[10px] text-slate-500 truncate">{a.subject || a.channel_label || a.channel}</p>
                    </div>
                    <button data-testid={`pullback-${a.id}`} onClick={() => pullBack(a.id)} className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded-md border border-red-500/25 text-red-300 hover:bg-red-500/10 transition-all">Pull back</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {recap && (
            <div data-testid="daily-recap" className="rounded-2xl border border-white/10 bg-[#121824] p-6 kz-fade-up">
              <div className="flex items-center gap-2 text-slate-300"><FileText size={16} className="text-emerald-400" /><span className="text-xs font-bold uppercase tracking-wider">Daily send recap</span></div>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed"><b className="text-emerald-400">{recap.count}</b> message{recap.count === 1 ? "" : "s"} auto-sent in the last 24h.</p>
              {Object.keys(recap.by_channel || {}).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {Object.entries(recap.by_channel).map(([ch, n]) => (
                    <span key={ch} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{ch}: <b className="text-slate-200">{n}</b></span>
                  ))}
                </div>
              )}
              {recap.note && <p className="text-[10px] text-slate-500 mt-2 leading-snug">{recap.note}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
